/* Render a typed Predicate AST into the natural-language sentence
 * the Rule Builder shows. The AST is the source of truth; this
 * function is the one-way projection. No string parsing anywhere —
 * the natural sentence is derived. */

import type {
  Predicate, NodePattern, Literal, ExtendedTerm, AggTerm, SetLiteral, RangeLiteral,
} from "../types/rule";

const AGG_LABEL: Record<AggTerm["op"], string> = {
  sum: "Σ", avg: "avg", count: "count", min: "min", max: "max",
};

function renderNodePattern(p: NodePattern): string {
  if (p.filterAttr && p.filterValue !== undefined) {
    return `${p.nodeKind}(${p.filterAttr}=${String(p.filterValue)})`;
  }
  return p.nodeKind;
}

function renderLiteral(l: Literal): string {
  if (l.type === "string") return String(l.value);
  if (l.type === "boolean") return String(l.value);
  return `${l.value}${l.unit ? ` ${l.unit}` : ""}`;
}

function renderSet(s: SetLiteral): string {
  return `{${s.values.join(", ")}}`;
}

function renderRange(r: RangeLiteral): string {
  return `[${r.lo}–${r.hi}${r.unit ? ` ${r.unit}` : ""}]`;
}

function renderAgg(a: AggTerm): string {
  const op = AGG_LABEL[a.op];
  const overTxt = renderNodePattern(a.over);
  const viaTxt = a.via
    ? ` ${a.via.relation} ${renderNodePattern(a.via.target)}`
    : "";
  if (a.op === "count") {
    return `${op}(${a.distinct ? "distinct " : ""}${overTxt}${viaTxt})`;
  }
  if (a.field) {
    return `${op} ${overTxt}.${a.field.path.split(".")[1] ?? a.field.path}${viaTxt}`;
  }
  return `${op}(${overTxt}${viaTxt})`;
}

function renderTerm(t: ExtendedTerm): string {
  if (t.kind === "lit")   return renderLiteral(t);
  if (t.kind === "field") return `${t.path}${t.unit ? ` ${t.unit}` : ""}`;
  return renderAgg(t);
}

function renderObject(o: NodePattern | Literal): string {
  return o.kind === "pattern" ? renderNodePattern(o) : renderLiteral(o);
}

export function predicateToText(p: Predicate): string {
  switch (p.kind) {
    case "cmp":
      return `${renderTerm(p.lhs)} ${p.op} ${renderTerm(p.rhs)}`;
    case "in_range":
      return `${renderTerm(p.value)} ∈ ${renderRange(p.range)}`;
    case "not_in_range":
      return `${renderTerm(p.value)} ∉ ${renderRange(p.range)}`;
    case "in_set":
      return `${renderTerm(p.value)} ∈ ${renderSet(p.set)}`;
    case "not_in_set":
      return `${renderTerm(p.value)} ∉ ${renderSet(p.set)}`;
    case "graph_pattern":
      return `${renderNodePattern(p.subject)} ${p.relation} ${renderObject(p.object)}`;
    case "exists":
      return `∃ ${renderNodePattern(p.pattern.subject)} ${p.pattern.relation} ${renderObject(p.pattern.object)}`;
    case "forbidden":
      return `∄ ${renderNodePattern(p.pattern.subject)} ${p.pattern.relation} ${renderObject(p.pattern.object)}`;
    case "clearance":
      return `clearance ${p.subjectKind}${p.roomKind ? `@${p.roomKind}` : ""} ≥ ${p.minM} m${p.directions?.length ? ` (${p.directions.join("/")})` : ""}`;
    case "sightline":
      return `sightline ${p.fromKind} → ${p.toKind}${p.roomKind ? ` in ${p.roomKind}` : ""}${p.obstacleKinds?.length ? ` ⊥ {${p.obstacleKinds.join(", ")}}` : ""}`;
    case "work_triangle":
      return `work-triangle Σ(${p.legs.join(" + ")}) ∈ [${p.minM}, ${p.maxM}] m in ${p.roomKind}`;
    case "egress_path":
      return `egress ${p.subjectKind} width ≥ ${p.minWidthM} m, max ${p.maxDistanceM} m`;
    case "and":
      return `(${predicateToText(p.left)}) ∧ (${predicateToText(p.right)})`;
    case "or":
      return `(${predicateToText(p.left)}) ∨ (${predicateToText(p.right)})`;
    case "not":
      return `¬(${predicateToText(p.inner)})`;
  }
}
